import { Shield, AlertTriangle, Clock, CreditCard } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FadeIn, StaggerChildren, StaggerItem } from '@/components/motion';
import { cn } from '@/lib/utils/cn';
import { slaPlans, priorityClassification, slaCredits, downtimeLimits } from '../data/sla-data';

export function SlaSection() {
  const metricKeys = Object.keys(slaPlans[0].metrics);

  return (
    <div className="space-y-8">
      {/* Plans comparison table */}
      <FadeIn direction="up">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle>Niveles de Servicio por Plan</CardTitle>
            </div>
            <CardDescription>
              Comparación de métricas SLA entre nuestros planes de soporte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">
                      Metrica
                    </th>
                    {slaPlans.map((plan) => (
                      <th
                        key={plan.name}
                        className={cn(
                          'pb-3 px-4 text-center font-semibold',
                          plan.highlighted && 'text-primary'
                        )}
                      >
                        <div>{plan.name}</div>
                        <div className="mt-0.5 text-xs font-normal text-muted-foreground">
                          {plan.price}
                        </div>
                        {plan.highlighted && (
                          <Badge variant="default" className="mt-1 text-[10px]">
                            Popular
                          </Badge>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {metricKeys.map((metric, idx) => (
                    <tr
                      key={metric}
                      className={cn('border-b last:border-0', idx % 2 === 0 ? 'bg-muted/30' : '')}
                    >
                      <td className="py-2.5 pr-4 font-medium">{metric}</td>
                      {slaPlans.map((plan) => (
                        <td
                          key={plan.name}
                          className={cn(
                            'py-2.5 px-4 text-center text-muted-foreground',
                            plan.highlighted && 'font-medium text-foreground'
                          )}
                        >
                          {plan.metrics[metric]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">BOT = Bolivia Time (UTC-4)</p>
          </CardContent>
        </Card>
      </FadeIn>

      {/* Priority classification */}
      <FadeIn direction="up" delay={0.1}>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <CardTitle>Clasificación de Prioridades</CardTitle>
            </div>
            <CardDescription>Como se clasifican los incidentes según su impacto</CardDescription>
          </CardHeader>
          <CardContent>
            <StaggerChildren className="space-y-3">
              {priorityClassification.map((item) => {
                const priorityColors: Record<string, string> = {
                  P1: 'bg-red-500',
                  P2: 'bg-orange-500',
                  P3: 'bg-yellow-500',
                  P4: 'bg-blue-500',
                };
                return (
                  <StaggerItem key={item.priority}>
                    <div className="flex items-start gap-3 rounded-lg border p-3">
                      <span
                        className={cn(
                          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white',
                          priorityColors[item.priority]
                        )}
                      >
                        {item.priority}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{item.name}</span>
                        </div>
                        <p className="mt-0.5 text-sm text-muted-foreground">{item.description}</p>
                        <p className="mt-1 text-xs text-muted-foreground/70">
                          Ejemplo: {item.example}
                        </p>
                      </div>
                    </div>
                  </StaggerItem>
                );
              })}
            </StaggerChildren>
          </CardContent>
        </Card>
      </FadeIn>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Downtime limits */}
        <FadeIn direction="up" delay={0.15}>
          <Card className="h-full">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Limites de Indisponibilidad</CardTitle>
              </div>
              <CardDescription>
                Tiempo máximo de caida permitido según la disponibilidad contratada
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="pb-2 pr-4 text-left font-medium text-muted-foreground">
                        Disponibilidad
                      </th>
                      <th className="pb-2 px-4 text-center font-medium text-muted-foreground">
                        Max/mes
                      </th>
                      <th className="pb-2 pl-4 text-center font-medium text-muted-foreground">
                        Max/ano
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {downtimeLimits.map((row) => (
                      <tr key={row.availability} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 font-semibold text-primary">
                          {row.availability}
                        </td>
                        <td className="py-2.5 px-4 text-center text-muted-foreground">
                          {row.monthlyDowntime}
                        </td>
                        <td className="py-2.5 pl-4 text-center text-muted-foreground">
                          {row.yearlyDowntime}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </FadeIn>

        {/* SLA credits */}
        <FadeIn direction="up" delay={0.2}>
          <Card className="h-full">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-green-500" />
                <CardTitle>Creditos por Incumplimiento</CardTitle>
              </div>
              <CardDescription>
                Porcentaje de la factura mensual que se acredita si no se cumple el SLA
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="pb-2 pr-4 text-left font-medium text-muted-foreground">
                        Disponibilidad real
                      </th>
                      <th className="pb-2 pl-4 text-center font-medium text-muted-foreground">
                        Credito
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {slaCredits.map((row, idx) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 text-muted-foreground">{row.condition}</td>
                        <td className="py-2.5 pl-4 text-center font-semibold text-primary">
                          {row.credit}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 space-y-1">
                <p className="text-xs text-muted-foreground">Condiciones:</p>
                <ul className="space-y-0.5 text-xs text-muted-foreground/80">
                  <li>- Solicitar dentro de los 30 días del incidente</li>
                  <li>- Se aplica como descuento en la siguiente factura</li>
                  <li>- Máximo acumulable: 50% de la factura mensual</li>
                  <li>- No aplica si el cliente esta en mora</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </FadeIn>
      </div>
    </div>
  );
}
